import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Building2 } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import useJsonLd from "@/hooks/useJsonLd";
import ContourBackground from "@/components/ContourBackground";
import Reveal from "@/components/Reveal";
import { getMetroBySlug, US_STATES } from "@/data/usLocations";
import { SERVICES } from "@/data/services";
import { CAD_SERVICES } from "@/data/cadServices";
import { metroLd, breadcrumb } from "@/lib/jsonLd";

export default function MetroPage() {
  const { metroSlug } = useParams();
  const metro = getMetroBySlug(metroSlug);
  const state = metro ? US_STATES.find((s) => s.abbr === metro.state) : null;

  useSeo({
    title: metro
      ? `${metro.name} CAD Drafting & Survey CAD · Tri-Axis XYZ`
      : "Coverage — Tri-Axis XYZ",
    description: metro
      ? `Dedicated CAD outsourcing team for the ${metro.name}. ALTA, boundary, topo, plats, MEP/BIM, structural, civil and 3D drafting across ${metro.anchor}.`
      : "",
  });

  useJsonLd(metro ? metroLd(metro) : null, `metro-${metroSlug}`);
  useJsonLd(
    metro
      ? breadcrumb([
          { name: "Home", path: "/" },
          { name: "Coverage", path: "/cad-services" },
          { name: metro.name, path: `/metros/${metro.slug}` },
        ])
      : null,
    `metro-bc-${metroSlug}`,
  );

  if (!metro) return <Navigate to="/cad-services" replace />;

  return (
    <div data-testid="page-metro">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-paper-dark/40">
        <ContourBackground variant="light" opacity={0.5} seed={metro.slug.length + 11} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <nav className="mono text-[10px] tracking-[0.22em] uppercase text-topo flex items-center gap-2">
              <Link to="/cad-services" className="hover:text-emerald-700">
                Coverage
              </Link>
              <span>›</span>
              <span>Metros</span>
              <span>›</span>
              <span className="text-slate-900">{metro.name}</span>
            </nav>
            <h1
              data-testid="metro-page-title"
              className="display mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[1.02]"
            >
              <span className="text-emerald-700">{metro.name}</span>{" "}
              <span className="block">CAD drafting partner</span>
            </h1>
            <p className="mt-5 max-w-3xl text-base sm:text-lg text-slate-600 leading-relaxed">
              From boundary plats to MEP coordination, Tri-Axis XYZ supports
              firms across <strong>{metro.anchor}</strong>. Stamped-ready
              outputs, fixed-fee or hourly retainers, ISO-grade QC.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to={`/contact?country=USA&state=${encodeURIComponent(metro.name)}&code=${metro.state}`}
                data-testid="metro-cta-quote"
                className="inline-flex items-center gap-2 rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-5 py-3"
              >
                Get a {metro.state} quote <ArrowRight className="h-4 w-4" />
              </Link>
              {state && (
                <Link
                  to={`/cad-services/${state.slug}`}
                  className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-5 py-3 bg-white"
                >
                  See full {state.state} coverage
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Counties */}
      {state && (
        <section className="bg-white border-b border-slate-200 py-14">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <Reveal>
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
                Anchor counties · {metro.name}
              </div>
              <h2 className="display mt-2 text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">
                Where we run most {metro.name} projects
              </h2>
            </Reveal>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {state.counties.slice(0, 6).map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/cad-services/${state.slug}/${c.slug}`}
                    data-testid={`metro-county-${c.slug}`}
                    className="group block border border-slate-200 bg-white hover:border-emerald-600 hover:shadow-sm transition p-4"
                  >
                    <div className="flex items-center gap-2 text-emerald-700">
                      <Building2 className="h-4 w-4" />
                      <span className="mono text-[10px] tracking-[0.18em] uppercase">
                        {state.abbr}
                      </span>
                    </div>
                    <div className="mt-1 font-extrabold text-slate-900">{c.name}</div>
                    <div className="mt-1 text-xs text-slate-600">Seat: {c.seat}</div>
                    <div className="mt-3 inline-flex items-center text-xs font-semibold text-emerald-700">
                      County detail <ArrowRight className="ml-1 h-3 w-3" />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Service mix */}
      <section className="bg-paper-dark/30 border-b border-slate-200 py-14">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <Reveal>
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
              🧭 Land-Survey CAD · {metro.name}
            </div>
            <ul className="mt-4 grid grid-cols-2 gap-2 text-sm">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-slate-700 hover:text-emerald-700">
                    {s.emoji} {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal>
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
              📐 CAD Beyond Survey · {metro.name}
            </div>
            <ul className="mt-4 grid grid-cols-2 gap-2 text-sm">
              {CAD_SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/cad-techies#cad-${s.slug}`}
                    className="text-slate-700 hover:text-emerald-700"
                  >
                    {s.emoji} {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
